import { useEffect, useState } from 'react';
import ChartBarKPI from '@/components/KPI/ChartBarKPI.tsx';
import { ChartBarProps } from '@/components/KPI/ChartBar.tsx';
import {
  IconInfoCircle,
  IconPhotoCheck,
  IconPhotoEdit,
  IconBrandPnpm,
  IconCarCrash,
  IconCirclesRelation,
  IconChartArrowsVertical,
} from '@tabler/icons-react';
import {
  useGetKPIElementDamageLevelCricity,
  useGetKPIElementDamageLink,
  useGetKPIMoteur,
  useGetKPIMoteurDamage,
  useGetKPIMoteurElement,
} from '@/hooks/ai.hooks.ts';
import { KpiFilterRequest } from '@/api/entities/kpi.ts';
import dayjs from 'dayjs';
import { Select } from '@/components/Controls/Select';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import Loader from '@/components/Loading/Loader';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import { formatDayjsDate } from '@/utils/date.utils';
import { InfosMessage } from '@/components/Message/InfosMessage';

const initialKpiFilterRequest: KpiFilterRequest = {};

export default function KPIMoteur() {
  const { t } = useTranslation('kpi');
  const [period, setPeriod] = useState<boolean>(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [totalPhotosModerate, setTotalPhotosModerate] = useState<number>(0);

  const [settings1, setSettings1] = useState<ChartBarProps>();
  const [settings2, setSettings2] = useState<ChartBarProps>();
  const [settings3, setSettings3] = useState<ChartBarProps>();
  const [settings4, setSettings4] = useState<ChartBarProps>();
  const [settings5, setSettings5] = useState<ChartBarProps>();
  const [settings6, setSettings6] = useState<ChartBarProps>();

  const [filters, setFilters] = useState<KpiFilterRequest>(
    initialKpiFilterRequest
  );
  const [filtersElement, setFiltersElement] = useState<KpiFilterRequest>(
    initialKpiFilterRequest
  );
  const [filtersDamage, setFiltersDamage] = useState<KpiFilterRequest>(
    initialKpiFilterRequest
  );
  const [filtersElementDamageLink, setFiltersElementDamageLink] =
    useState<KpiFilterRequest>(initialKpiFilterRequest);
  const [
    filtersElementDamageLevelCricity,
    setFiltersElementDamageLevelCricity,
  ] = useState<KpiFilterRequest>(initialKpiFilterRequest);

  const { data, isLoading } = useGetKPIMoteur(filters);

  const { data: dataElements } = useGetKPIMoteurElement(filtersElement);
  const { data: dataDamage } = useGetKPIMoteurDamage(filtersDamage);
  const { data: dataElementDamageLink } = useGetKPIElementDamageLink(
    filtersElementDamageLink
  );
  const { data: dataElementDamageLevelCricity } =
    useGetKPIElementDamageLevelCricity(filtersElementDamageLevelCricity);

  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { uniqueDamagesSelectOptions } = useGetModerationDamages();

  const optionElements = equipmentsSelectOptions;
  const optionsDmgList = uniqueDamagesSelectOptions;

  const [valueElement, setValueElement] = useState<string>();
  const [valueDamage, setValueDamage] = useState<string>();
  const [valueElementLink, setValueElementLink] = useState<string>();
  const [valueDamageLink, setValueDamageLink] = useState<string>();
  const [valueElementLevelCricity, setValueElementLevelCricity] =
    useState<string>();
  const [valueDamageLevelCricity, setValueDamageLevelCricity] =
    useState<string>();

  const moderatedPhotosText =
    totalPhotosModerate > 1
      ? t('moderatedPhotos').toLowerCase()
      : t('moderatedPhoto').toLowerCase();

  useEffect(() => {
    setFiltersElement({
      ...filtersElement,
      target: valueElement,
    });
  }, [valueElement]);

  useEffect(() => {
    setFiltersDamage({
      ...filtersDamage,
      target: valueDamage,
    });
  }, [valueDamage]);

  useEffect(() => {
    setFiltersElementDamageLink({
      ...filtersElementDamageLink,
      targetEl: valueElementLink,
      targetDmg: valueDamageLink,
    });
  }, [valueElementLink, valueDamageLink]);

  useEffect(() => {
    setFiltersElementDamageLevelCricity({
      ...filtersElementDamageLevelCricity,
      targetEl: valueElementLevelCricity,
      targetDmg: valueDamageLevelCricity,
    });
  }, [valueElementLevelCricity, valueDamageLevelCricity]);

  useEffect(() => {
    const kpi = dataElementDamageLevelCricity?.data;
    if (kpi && kpi.result) {
      setSettings6({
        data: kpi.result.totalKpiLevelCricity,
        colors: ['#FF8080', '#FEDD5A'],
        categories: [t('attributionsErrors')],
        height: 180,
      });
    }
  }, [dataElementDamageLevelCricity, t]);

  useEffect(() => {
    const kpi = dataElementDamageLink?.data;
    if (kpi && kpi.result) {
      setSettings5({
        data: kpi.result.totalKpiElementsDamagesLink,
        colors: ['#FF8080', '#FEDD5A'],
        categories: [t('nonDetections'), t('linkErrors')],
        height: 180,
      });
    }
  }, [dataElementDamageLink, t]);

  useEffect(() => {
    const kpi = dataElements?.data;
    if (kpi && kpi.result) {
      setSettings3({
        data: kpi.result.totalKpiElements,
        colors: ['#FF8080', '#FEDD5A', '#7EE4AC'],
        categories: [
          t('falsePositives'),
          t('nonDetections'),
          t('attributionsErrors'),
        ],
        height: 270,
      });
    }
  }, [dataElements, t]);

  useEffect(() => {
    const kpi = dataDamage?.data;
    if (kpi && kpi.result) {
      setSettings4({
        data: kpi.result.totalKpiDamages,
        colors: ['#FF8080', '#FEDD5A', '#7EE4AC'],
        categories: [
          t('falsePositives'),
          t('nonDetections'),
          t('attributionsErrors'),
        ],
        height: 270,
      });
    }
  }, [dataDamage, t]);

  useEffect(() => {
    const kpi = data?.data;
    if (kpi && kpi.result) {
      setTotalPhotosModerate(kpi.result.totalPhotosModerate ?? 0);
      setSettings1({
        data: kpi.result.totalResult,
        colors: ['#FF8080', '#FEDD5A'],
        categories: [t('imagesWithoutRetouch'), t('imagesWithRetouch')],
        height: 180,
      });
      setSettings2({
        data: kpi.result.totalDetailResult,
        colors: [
          '#FF8080',
          '#FEDD5A',
          '#7EE4AC',
          '#80C1FF',
          '#FF8080',
          '#FEDD5A',
          '#7EE4AC',
          '#80C1FF',
        ],
        categories: [
          t('imagesWithoutRetouchForEquipments'),
          t('imagesWithRetouchForEquipments'),
          t('imagesWithoutRetouchForDamages'),
          t('imagesWithRetouchForDamages'),
          t('imagesWithoutRetouchForEquipmentDamageLink'),
          t('imagesWithRetouchForEquipmentDamageLink'),
          t('imagesWithoutRetouchForDamageLevelCricity'),
          t('imagesWithRetouchForDamageLevelCricity'),
        ],
        height: 550,
      });
    }
  }, [data, t]);

  useEffect(() => {
    if (period) {
      const formattedStartDate = formatDayjsDate(startDate);
      const formattedEndDate = formatDayjsDate(endDate);

      setFilters({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      setFiltersElement({
        ...filtersElement,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      setFiltersDamage({
        ...filtersDamage,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      setFiltersElementDamageLink({
        ...filtersElementDamageLink,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      setFiltersElementDamageLevelCricity({
        ...filtersElementDamageLevelCricity,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    } else {
      setFilters(initialKpiFilterRequest);
      setFiltersElement({
        ...filtersElement,
        ...initialKpiFilterRequest,
      });
      setFiltersElement({
        ...filtersDamage,
        ...initialKpiFilterRequest,
      });
      setFiltersElementDamageLink({
        ...filtersElementDamageLink,
        ...initialKpiFilterRequest,
      });
      setFiltersElementDamageLevelCricity({
        ...filtersElementDamageLevelCricity,
        ...initialKpiFilterRequest,
      });
    }
  }, [period, startDate, endDate]);

  useEffect(() => {
    const currentDate = new Date();
    const currentDay = currentDate.getDay(); // Sunday is 0, Monday is 1, and so on.
    const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay; // Adjust for Sunday being 0
    const mondayDate = new Date(currentDate);
    mondayDate.setDate(currentDate.getDate() + diffToMonday);

    setStartDate(mondayDate);
    setEndDate(currentDate);
  }, []);

  return (
    <>
      <div>
        <div className="card">
          <div className="card-body">
            <InfosMessage
              containerClassName="py-2 m-auto mb-4 border border-dashed  border-primary bg-white"
              icon={<IconInfoCircle className="text-primary" stroke={2} />}
              messageClassName="text-sm text-primary"
              message="Veuillez cliquer sur la case à cocher période choisit pour activer le filter par date"
            />
            <div className="flex flex-row gap-4 justify-center">
              <label className="form-label flex items-center text-nowrap w-fit gap-2">
                <input
                  className="checkbox"
                  name="check"
                  type="checkbox"
                  checked={period}
                  onChange={(e) => setPeriod(e.target.checked)}
                />{' '}
                {t('common:chosenPeriod')}
              </label>
              <div className="flex items-center gap-2">
                <div className="text-2sm text-gray-700 font-medium">
                  {t('common:from').toLowerCase()}
                </div>
                <DateTimePicker
                  useTime={false}
                  disabled={!period}
                  value={dayjs(startDate)}
                  onChange={(value) => setStartDate(value.toDate())}
                  disablePast={false}
                  numberOfAllowedFutureDays={0}
                  maxDate={dayjs(endDate)}
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="text-2sm text-gray-700 font-medium">
                  {t('common:to').toLowerCase()}
                </div>
                <DateTimePicker
                  useTime={false}
                  disabled={!period}
                  value={dayjs(endDate)}
                  onChange={(value) => setEndDate(value.toDate())}
                  disablePast={false}
                  numberOfAllowedFutureDays={1}
                  minDate={dayjs(startDate)}
                />
              </div>
            </div>
          </div>
        </div>
        <ChartBarKPI
          settings={settings1}
          icon={<IconPhotoCheck className="text-primary" stroke={2} />}
          title={t('imagesWithoutRetouch')}
          subTitle={`${totalPhotosModerate} ${moderatedPhotosText}`}
        />
        <ChartBarKPI
          settings={settings2}
          icon={<IconPhotoEdit className="text-primary" stroke={2} />}
          title={t('imagesWithOrWithoutErrorsOnAtLeastOnePoint')}
          subTitle={`${totalPhotosModerate} ${moderatedPhotosText}`}
        />
        <ChartBarKPI
          filterContent={
            <Select
              containerClassName="mb-2 w-64"
              selectClassName="bg-white"
              placeholder={t('common:chooseAnEquipment')}
              options={optionElements}
              onChange={(e) => setValueElement(e.target.value)}
              defaultValue={'-1'}
            />
          }
          settings={settings3}
          icon={<IconBrandPnpm className="text-primary" stroke={2} />}
          title={t('kpiOnEquipments')}
          subTitle={`${totalPhotosModerate} ${moderatedPhotosText}`}
        />
        <ChartBarKPI
          filterContent={
            <Select
              containerClassName="mb-2 w-64"
              selectClassName="bg-white"
              placeholder={t('common:chooseADamage')}
              options={optionsDmgList}
              onChange={(e) => setValueDamage(e.target.value)}
              defaultValue={'-1'}
            />
          }
          settings={settings4}
          icon={<IconCarCrash className="text-primary" stroke={2} />}
          title={t('kpiOnDamages')}
          subTitle={`${totalPhotosModerate} ${moderatedPhotosText}`}
        />
        <div>
          <ChartBarKPI
            filterContent={
              <div className="flex justify-end gap-3">
                <Select
                  containerClassName="mb-2 w-64"
                  selectClassName="bg-white"
                  placeholder={t('common:chooseAnEquipment')}
                  options={optionElements}
                  onChange={(e) => setValueElementLink(e.target.value)}
                  defaultValue={'-1'}
                />
                <Select
                  containerClassName="mb-2 w-64"
                  selectClassName="bg-white"
                  placeholder={t('common:chooseADamage')}
                  options={optionsDmgList}
                  onChange={(e) => setValueDamageLink(e.target.value)}
                  defaultValue={'-1'}
                />
              </div>
            }
            settings={settings5}
            icon={<IconCirclesRelation className="text-primary" stroke={2} />}
            title={t('kpiOnEquipmentDamageLink')}
            subTitle={`${totalPhotosModerate} ${moderatedPhotosText}`}
          />
        </div>
        <ChartBarKPI
          filterContent={
            <div className="flex justify-end gap-3">
              <Select
                containerClassName="mb-2 w-64"
                selectClassName="bg-white"
                placeholder={t('common:chooseAnEquipment')}
                options={optionElements}
                onChange={(e) => setValueElementLevelCricity(e.target.value)}
                defaultValue={'-1'}
              />
              <Select
                containerClassName="mb-2 w-64"
                selectClassName="bg-white"
                placeholder={t('common:chooseADamage')}
                options={optionsDmgList}
                onChange={(e) => setValueDamageLevelCricity(e.target.value)}
                defaultValue={'-1'}
              />
            </div>
          }
          settings={settings6}
          icon={<IconChartArrowsVertical className="text-primary" stroke={2} />}
          title={t('kpiOnDamageLevelCricity')}
          subTitle={`${totalPhotosModerate} ${moderatedPhotosText}`}
        />
      </div>
      {isLoading && <Loader />}
    </>
  );
}
